import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { MinimumDistanceSlider } from '../issues/filters/MinimumDistanceSlider'
import DropdownTreeView, { DropdownTreeViewItems } from '../issues/filters/DropdownTreeView'
import UserEventApi from '../../api/UserEventApi'
import MarketplacesApi from '../../api/MarketplacesApi'
import { Marketplace } from '../../types/Marketplace'
import { companyFiltersAtom } from '../../atoms/companyAtom'
import { CompanyEntity } from '../../types/CompanyEntity'
import DropdownSelect from '../components/DropdownSelect'
import Grid from '@mui/material/Grid'
import ToggleWatchLists from '../components/ToggleWatchLists'
import { translateMillistreamSector } from '../../utils/millistreamSectors'

interface CompanyFiltersProps {
    companies: CompanyEntity[]
}

export const getSectorId = (sector1: string, sector2: string | null) => {
    return `${sector1}__${sector2}`
}

const getSectors = (companies: CompanyEntity[]) => {
    const sectors: DropdownTreeViewItems = {}

    companies.forEach((company) => {
        if (company.sector1 === null) return

        if (!sectors[company.sector1]) {
            sectors[company.sector1] = []
        }

        const id = getSectorId(company.sector1, company.sector2)

        if (sectors[company.sector1].find((level) => level.id === id)) return

        sectors[company.sector1].push({
            id,
            label: translateMillistreamSector(2, company.sector2),
        })
    })

    return sectors
}

export const CompaniesFilters = ({ companies }: CompanyFiltersProps) => {
    const { t } = useTranslation()
    const [filters, setFilters] = useAtom(companyFiltersAtom)
    const sectors = useMemo(() => getSectors(companies), [companies])
    const [allMarketplaces, setAllMarketplaces] = useState<Marketplace[]>([])
    const [filteredMarketplaces, setFilteredMarketplaces] = useState<DropdownTreeViewItems>({})
    const [marketplacesWithCompanies, setMarketplacesWithCompanies] = useState<string[]>([])

    const listedAlternatives = useMemo(
        () => [
            { id: 'all', label: t('pages.companies.filters.showAllCompanies') },
            { id: 'only-listed', label: t('pages.companies.filters.onlyListedCompanies') },
            { id: 'only-unlisted', label: t('pages.companies.filters.onlyDelistedCompanies') },
        ],
        [t],
    )

    useEffect(() => {
        const fetchMarkets = async () => {
            const api = new MarketplacesApi()
            const markets = await api.allMarketplaces()

            setAllMarketplaces(markets)
        }

        void fetchMarkets()
    }, [])

    useEffect(() => {
        if (!companies.length) return

        const marketplaces = new Set<string>()

        companies.forEach((company) => {
            marketplaces.add(company.marketplaceId)
        })

        setMarketplacesWithCompanies([...marketplaces])
    }, [companies])

    useEffect(() => {
        const groupedMarketLists = allMarketplaces
            .filter((marketplace) => !!marketplace.listName && marketplacesWithCompanies.includes(marketplace.id))
            .reduce((acc, val) => {
                const key = val.marketplaceName
                const arr = acc[key] || []

                return {
                    ...acc,
                    [key]: [
                        ...arr,
                        {
                            id: val.id,
                            label: val.listName,
                        },
                    ],
                }
            }, {})

        setFilteredMarketplaces(groupedMarketLists)
    }, [allMarketplaces, marketplacesWithCompanies])

    const handleFilterChange = useCallback(
        async (filterType: string, selectedItems: boolean | (string | Date)[]) => {
            const userEventApi = new UserEventApi()
            await userEventApi.logEvent(UserEventApi.EVENT_TYPES.FILTER_COMPANIES, {
                filterType,
                selectedItems,
            })
            setFilters((prevFilters) => ({
                ...prevFilters,
                [filterType]: { value: selectedItems },
            }))
        },
        [setFilters],
    )

    if (companies.length === 0) return null

    return (
        <Grid
            container
            spacing={3}
            sx={{
                pb: 6,
            }}
        >
            <Grid size={{ xs: 6, sm: 3 }}>
                <DropdownSelect
                    label={t('pages.companies.filters.delistedLabel')}
                    items={listedAlternatives}
                    selectedItems={filters.listed.value}
                    onChange={(selectedItems) => void handleFilterChange('listed', selectedItems)}
                />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
                <MinimumDistanceSlider
                    label={t('pages.companies.filters.marketCap')}
                    allSelectedMessage={t('site.filters.allCompanies')}
                    value={filters.marketSize.value as [number, number]}
                    includeGreaterValues={filters.marketSize.includeGreaterValues}
                    valueLimits={[0, filters.marketSize.maxValue]}
                    onChange={(value, isIncludeGreaterValues) =>
                        setFilters((prevFilters) => ({
                            ...prevFilters,
                            marketSize: {
                                ...prevFilters.marketSize,
                                value: value,
                                hasMaxValueSet: isIncludeGreaterValues,
                            },
                        }))
                    }
                    unit='MSEK'
                    includeInputFields
                />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
                <DropdownTreeView
                    items={sectors}
                    label={t('site.filters.industry')}
                    pluralLabel={t('site.filters.industries')}
                    selectedItems={filters.sectors.value}
                    onChange={(selectedItems) => void handleFilterChange('sectors', selectedItems)}
                />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
                <DropdownTreeView
                    label={t('site.filters.lists')}
                    pluralLabel={t('site.filters.lists')}
                    items={filteredMarketplaces}
                    selectedItems={filters.marketplaces.value}
                    onChange={(selectedItems) => void handleFilterChange('marketplaces', selectedItems)}
                />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
                <ToggleWatchLists
                    onChange={(v) => void handleFilterChange('onlyInWatchLists', v)}
                    value={filters.onlyInWatchLists.value}
                />
            </Grid>
        </Grid>
    )
}
